import { useQuery } from '@tanstack/react-query';
import { TFunction } from 'i18next/typescript/t';
import { useMemo } from 'react';

import { PointCloudModel } from '../../../../typings/api/skymap/rest/v0/.common';
import { SkyMapAxiosServiceFactory } from '../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../utils/variables';
import { ProjectGeodataManager } from '../../viewer/project-geodata-manager';
import { useProjectSnapshot } from './use-project-snapshot';

export function useProjectPointClouds() {
  const { projectId: rawProjectId } = useProjectSnapshot();
  const projectId = useMemo(() => rawProjectId ?? undefined, [rawProjectId]);

  const { data } = useQuery({
    queryKey: ['getProjectPointClouds', projectId],
    enabled: !!projectId,
    queryFn: async () => {
      if (!isDefined(projectId)) {
        return [];
      }
      const response = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV0()
        .getPointClouds({
          path: { projectId },
        });

      return response.data;
    },
    networkMode: 'always',
    retry: false,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 0,
  });

  return data;
}

export function getTimelinePointCloudInfo(pointCloud: PointCloudModel, t: TFunction) {
  const timelineContainer = ProjectGeodataManager.instance()
    .getTimelineContainers()
    .find((x) => x.pointCloud === pointCloud.id);

  if (!timelineContainer) {
    return;
  }

  const activeTimeline = ProjectGeodataManager.instance().getActiveTimelineContainer();
  let displayName = `${t('terrainModel.timeline', { ns: 'skyviewAssetMenu' })} ${timelineContainer.containerIndex.toString()} (${pointCloud.fileName} ${
    timelineContainer.displayDate
  })`;

  if (activeTimeline && timelineContainer.id === activeTimeline.id) {
    displayName = displayName + ` (${t('timeline.current', { ns: 'skyview' })})`;
  }

  return {
    id: timelineContainer.containerIndex.toString(),
    index: timelineContainer.containerIndex,
    geodataId: pointCloud.geodataId,
    pointCloudId: pointCloud.id,
    name: displayName,
  };
}
